import { Alert as MuiAlert, Box, Button, Grid, MenuItem, Stack, TextField } from '@mui/material';
import { useMemo, useState } from 'react';

import { PageHeader } from '@opengov/components-page-header';
import { Download } from '@opengov/react-capital-assets';

import { SectionCard } from '../components/vcm/SectionCard';
import { CASE_STATUSES } from '../lib/vcm/constants';
/* MOCK DATA — replace with real /v2 API calls when backend is connected */
import { mockGetCases, mockGetClients } from '../mocks/vcm-mock-data';

export interface ExportPageProps {
  readonly title?: string;
  readonly description?: string;
  /** Accepted for caller compatibility; not used by the case export UI. */
  readonly datasets?: readonly string[];
}

const SCOPE_OPTIONS = ['My assigned', 'My organization', 'All permitted'] as const;
const ALL_EVENTS = 'All events';
const ALL_STATUSES = 'All';

/** Escape a CSV cell — wrap in quotes (doubling inner quotes) when it contains a comma, quote, or newline. */
function csvCell(value: string): string {
  return /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/** Build a CSV string from a header row + data rows, then trigger a client-side download. */
function downloadCsv(filename: string, headers: readonly string[], rows: readonly string[][]): void {
  const csv = [headers, ...rows].map((row) => row.map(csvCell).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function ExportPage({
  title = 'Export Cases',
  description = 'Export client and case records available to your role.',
}: ExportPageProps) {
  /* MOCK DATA */
  const clients = useMemo(() => mockGetClients(), []);
  const cases = useMemo(() => mockGetCases(), []);

  const [scope, setScope] = useState<string>(SCOPE_OPTIONS[2]);
  const [fileFormat, setFileFormat] = useState('CSV');
  const [eventFilter, setEventFilter] = useState(ALL_EVENTS);
  const [statusFilter, setStatusFilter] = useState(ALL_STATUSES);
  const [dateFrom, setDateFrom] = useState('');
  const [dateThrough, setDateThrough] = useState('');
  const [generated, setGenerated] = useState(false);

  const events = useMemo(
    () => [ALL_EVENTS, ...new Set(cases.map((c) => c.program ?? ''))],
    [cases],
  );

  /** Each row joins a client with one of their cases, honoring the event/status/date filters. */
  const rows = useMemo(() => {
    const out: string[][] = [];
    for (const client of clients) {
      const clientCases = cases.filter((c) => c.clientId === client.id);
      for (const kase of clientCases) {
        if (eventFilter !== ALL_EVENTS && kase.event !== eventFilter) continue;
        if (statusFilter !== ALL_STATUSES && kase.status !== statusFilter) continue;
        const intake = kase.dateOfDcmIntake ?? '';
        if (dateFrom && intake && intake < dateFrom) continue;
        if (dateThrough && intake && intake > dateThrough) continue;
        out.push([
          client.id,
          client.firstName,
          client.lastName,
          client.dateOfBirth ?? '',
          client.phoneHome ?? '',
          client.email ?? '',
          kase.county,
          kase.status,
          kase.tier,
          kase.assignedManager,
          kase.program ?? '',
          kase.programCode ?? '',
        ]);
      }
    }
    return out;
  }, [clients, cases, eventFilter, statusFilter, dateFrom, dateThrough]);

  const handleExport = () => {
    const today = new Date().toISOString().slice(0, 10);
    downloadCsv(
      `tcr-cases-${today}.${fileFormat === 'Excel' ? 'xlsx' : 'csv'}`,
      [
        'TCRID',
        'First Name',
        'Last Name',
        'DOB',
        'Phone',
        'Email',
        'County',
        'Status',
        'Tier',
        'Manager',
        'Event',
        'Program Code',
      ],
      rows,
    );
    setGenerated(true);
  };

  return (
    <Box
      sx={{
        p: (theme) => theme.spacing(3),
        display: 'flex',
        flexDirection: 'column',
        gap: (theme) => theme.spacing(2.5),
      }}
    >
      <PageHeader title={title} description={description} />

      <SectionCard title="Export Options">
        <Stack spacing={2}>
          {generated && (
            <MuiAlert severity="success" onClose={() => setGenerated(false)}>
              Case export prepared ({rows.length} {rows.length === 1 ? 'record' : 'records'}).
            </MuiAlert>
          )}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Export scope"
                value={scope}
                onChange={(e) => setScope(e.target.value)}
              >
                {SCOPE_OPTIONS.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="File format"
                value={fileFormat}
                onChange={(e) => setFileFormat(e.target.value)}
              >
                <MenuItem value="CSV">CSV</MenuItem>
                <MenuItem value="Excel">Excel workbook</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Event"
                value={eventFilter}
                onChange={(e) => setEventFilter(e.target.value)}
              >
                {events.map((ev) => (
                  <MenuItem key={ev} value={ev}>
                    {ev}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Case status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value={ALL_STATUSES}>All</MenuItem>
                {CASE_STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                type="date"
                fullWidth
                size="small"
                label="Date from"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                type="date"
                fullWidth
                size="small"
                label="Date through"
                value={dateThrough}
                onChange={(e) => setDateThrough(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
          </Grid>
          <Box>
            <Button
              variant="contained"
              startIcon={<Download />}
              disabled={rows.length === 0}
              onClick={handleExport}
            >
              Download {fileFormat}
            </Button>
          </Box>
        </Stack>
      </SectionCard>
    </Box>
  );
}
